import { getStateTaxRate } from '../data/states'

// 2024 Military Base Pay Table (Monthly) - Simplified version
const MILITARY_PAY_TABLE: Record<string, Record<number, number>> = {
  'E-1': { 0: 1917, 2: 1917, 4: 1917 },
  'E-2': { 0: 2149, 2: 2149, 4: 2149 },
  'E-3': { 0: 2259, 2: 2547, 4: 2547 },
  'E-4': { 0: 2503, 2: 2905, 4: 3056, 6: 3206 },
  'E-5': { 0: 2730, 2: 3206, 4: 3368, 6: 3530, 8: 3660 },
  'E-6': { 0: 2980, 2: 3524, 4: 3729, 6: 3891, 8: 4095, 10: 4225 },
  'E-7': { 0: 3445, 2: 3972, 4: 4177, 6: 4348, 8: 4519, 10: 4690, 12: 4860 },
  'E-8': { 0: 4957, 2: 5308, 4: 5479, 6: 5650, 8: 5857, 10: 6028, 12: 6199 },
  'E-9': { 0: 6068, 2: 6239, 4: 6410, 6: 6581, 8: 6752, 10: 6923, 12: 7094, 14: 7265, 16: 7436 },
  'O-1': { 0: 3826, 2: 4849, 4: 4849 },
  'O-2': { 0: 4407, 2: 5544, 4: 5982, 6: 6021 },
  'O-3': { 0: 5095, 2: 6398, 4: 6910, 6: 7332, 8: 7853, 10: 8110 },
  'O-4': { 0: 5807, 2: 7344, 4: 7896, 6: 8305, 8: 8837, 10: 9269, 12: 9614 },
  'O-5': { 0: 6745, 2: 8465, 4: 8998, 6: 9417, 8: 9836, 10: 10255, 12: 10674, 14: 11093 },
  'O-6': { 0: 8084, 2: 9493, 4: 9912, 6: 10331, 8: 10750, 10: 11169, 12: 11588, 14: 12007 },
}

const getBasePay = (payGrade: string, years: number): number => {
  const gradeTable = MILITARY_PAY_TABLE[payGrade]
  if (!gradeTable) return 0

  // Find the closest years bracket
  const yearsKeys = Object.keys(gradeTable).map(Number).sort((a, b) => a - b)
  let closestYear = yearsKeys[0]
  
  for (const yearKey of yearsKeys) {
    if (years >= yearKey) {
      closestYear = yearKey
    } else {
      break
    }
  }

  return gradeTable[closestYear] || 0
}

export const calculateMilitaryPaycheck = (
  payGrade: string,
  yearsOfService: number,
  filingStatus: 'single' | 'married' | 'head-of-household',
  state: string,
  bah: number,
  bas: number,
  specialPay: number
) => {
  // Get base pay from table
  const basePay = getBasePay(payGrade, yearsOfService)
  
  // Taxable income = base pay + special pay (BAH and BAS are NOT taxable)
  const taxableIncome = basePay + specialPay
  
  // Total gross pay includes all components
  const totalGrossPay = basePay + bah + bas + specialPay

  // Federal tax calculation (simplified progressive rates on taxable income only)
  let federalTax = 0
  const monthlyStandardDeduction = filingStatus === 'single' ? 1146 : filingStatus === 'married' ? 2292 : 1604
  const taxableAfterDeduction = Math.max(0, taxableIncome - monthlyStandardDeduction)

  // Simplified 2024 tax brackets (monthly)
  if (taxableAfterDeduction > 0) {
    if (taxableAfterDeduction <= 950) {
      federalTax = taxableAfterDeduction * 0.10
    } else if (taxableAfterDeduction <= 3867) {
      federalTax = 95 + (taxableAfterDeduction - 950) * 0.12
    } else if (taxableAfterDeduction <= 7292) {
      federalTax = 445 + (taxableAfterDeduction - 3867) * 0.22
    } else {
      federalTax = 1198 + (taxableAfterDeduction - 7292) * 0.24
    }
  }

  // State tax (on taxable income only)
  const stateTaxRate = getStateTaxRate(state)
  const stateTax = taxableIncome * stateTaxRate

  // Social Security (6.2% on taxable income, capped)
  const socialSecurityCap = 13733 // Monthly cap for 2024
  const socialSecurity = Math.min(taxableIncome, socialSecurityCap) * 0.062

  // Medicare (1.45% on taxable income)
  const medicare = taxableIncome * 0.0145

  // Total deductions
  const totalDeductions = federalTax + stateTax + socialSecurity + medicare

  // Net pay
  const netPay = totalGrossPay - totalDeductions

  return {
    basePay,
    bah,
    bas,
    specialPay,
    totalGrossPay,
    taxableIncome,
    federalTax,
    stateTax,
    socialSecurity,
    medicare,
    totalDeductions,
    netPay
  }
}
